/**
 * PublicCorporateMenu — Phase 101cw (Feb 2026)
 *
 * Page publique consultable/imprimable par une entreprise hôte (Novelis, etc.)
 * pour vérifier :
 *   • les prix Novelis (spécifiques ou remise globale) sur chaque produit
 *   • la table des tranches de participation CA mensuel (avec TVA)
 *
 * URL : /corporate/:id  (id = UUID du distributeur, non guessable)
 * API : GET /api/public/corporate/:id/menu  (pas d'authentification)
 *
 * L'entreprise hôte imprime via le bouton (window.print) — la page est
 * mise en page pour rendre correctement en A4 portrait, sans dépendance
 * à l'admin AgroManage.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, Loader2, Mail, ShieldCheck, Send } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const fmtCHF = (n) => `${Number(n || 0).toFixed(2)} CHF`;

export default function PublicCorporateMenu() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Phase 101et — État magic link
  const [needsMagicLink, setNeedsMagicLink] = useState(false);
  const [magicEmail, setMagicEmail] = useState("");
  const [magicSubmitting, setMagicSubmitting] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  const fetchMenu = async () => {
    setLoading(true);
    setError(null);
    setNeedsMagicLink(false);
    try {
      const { data } = await api.get(`/public/corporate/${id}/menu`);
      setData(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        setNeedsMagicLink(true);
      } else {
        setError(e?.response?.status === 404 ? "Menu introuvable" : "Erreur de chargement");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Phase 101et — Si redirigé depuis /corporate/verify-magic-link, un cookie
    // de session a été posé côté navigateur. On tente le fetch normalement.
    fetchMenu();
    // Empêcher indexation Google/DDG des pages Corporate (prix confidentiels)
    if (typeof document !== "undefined") {
      let m = document.querySelector('meta[name="robots"]');
      if (!m) {
        m = document.createElement("meta");
        m.name = "robots";
        document.head.appendChild(m);
      }
      m.content = "noindex,nofollow,noarchive";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const requestMagicLink = async (e) => {
    e?.preventDefault?.();
    if (!magicEmail.trim() || !magicEmail.includes("@")) {
      return;
    }
    setMagicSubmitting(true);
    try {
      await api.post(`/public/corporate/${id}/request-magic-link`, { email: magicEmail.trim() });
      setMagicSent(true);
    } catch (err) {
      // Réponse générique côté API — n'échoue quasi jamais
      setMagicSent(true);
    } finally {
      setMagicSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF4E9]">
        <Loader2 className="w-8 h-8 animate-spin text-[#8C6B2F]" />
      </div>
    );
  }
  if (needsMagicLink) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF4E9] p-6">
        <div className="w-full max-w-md bg-white shadow-lg rounded-lg overflow-hidden" data-testid="corporate-magic-gate">
          <div className="px-8 py-6 border-b-2 border-[#5B3A8C]">
            <img src="/company-name-sayonara.png" alt="Maison Thaï" className="h-8 mb-2" />
            <div className="text-xs uppercase tracking-wider text-[#5B3A8C] font-semibold flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Espace Corporate confidentiel
            </div>
          </div>
          {magicSent ? (
            <div className="px-8 py-8 text-center space-y-3" data-testid="magic-link-sent">
              <Send className="w-10 h-10 text-[#88A066] mx-auto" />
              <h1 className="text-lg font-semibold text-[#1A1C1A]">Vérifiez votre boîte mail</h1>
              <p className="text-sm text-[#575A57] leading-relaxed">
                Si <b>{magicEmail}</b> correspond à un domaine autorisé,
                un lien d'accès vient de vous être envoyé.
              </p>
              <p className="text-xs text-[#A3A6A3] mt-4">
                Le lien est valable 24 heures. Pensez à vérifier vos indésirables.
              </p>
              <button
                type="button"
                onClick={() => { setMagicSent(false); setMagicEmail(""); }}
                className="text-xs text-[#5B3A8C] underline hover:text-[#3F286B]"
                data-testid="magic-link-back"
              >
                ← Utiliser une autre adresse
              </button>
            </div>
          ) : (
            <form onSubmit={requestMagicLink} className="px-8 py-8 space-y-4">
              <div>
                <h1 className="text-lg font-semibold text-[#1A1C1A] mb-1">Accès réservé aux collaborateurs</h1>
                <p className="text-sm text-[#575A57] leading-relaxed">
                  Ce catalogue contient des prix négociés confidentiels.
                  Saisissez votre <b>email professionnel</b> pour recevoir un
                  lien d'accès valable 24 heures.
                </p>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-wider text-[#575A57] flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" /> Email professionnel
                </label>
                <Input
                  type="email"
                  required
                  placeholder="vous@entreprise.ch"
                  value={magicEmail}
                  onChange={(e) => setMagicEmail(e.target.value)}
                  disabled={magicSubmitting}
                  className="w-full"
                  data-testid="magic-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={magicSubmitting || !magicEmail.trim()}
                className="w-full bg-[#5B3A8C] hover:bg-[#3F286B] text-white"
                data-testid="magic-submit"
              >
                {magicSubmitting
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi…</>
                  : <><Send className="w-4 h-4 mr-2" /> Recevoir mon lien d'accès</>}
              </Button>
              <p className="text-[10px] text-[#A3A6A3] leading-snug text-center">
                Aucun mot de passe. Un simple clic dans l'email suffit à ouvrir le catalogue.
              </p>
            </form>
          )}
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF4E9]">
        <div className="text-center">
          <div className="text-2xl font-semibold text-[#7A1F1F] mb-2">{error}</div>
          <p className="text-sm text-[#575A57]">Vérifiez le lien fourni ou contactez Maison Thaï.</p>
        </div>
      </div>
    );
  }
  if (!data) return null;

  // Regroupe items par catégorie pour un tableau plus lisible
  const grouped = {};
  for (const it of data.items || []) {
    const key = it.category || "Autres";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(it);
  }

  return (
    <div className="min-h-screen bg-[#FBF4E9] py-8 print:py-0 print:bg-white">
      <style>{`
        @media print {
          @page { margin: 15mm; }
          .no-print { display: none !important; }
          .print-page { max-width: 100% !important; box-shadow: none !important; }
        }
      `}</style>

      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden print-page">
        {/* En-tête */}
        <div className="px-8 py-6 border-b-2 border-[#8C6B2F] flex items-center justify-between">
          <div>
            <img
              src="/company-name-sayonara.png"
              alt="Maison Thaï"
              className="h-8 mb-2"
            />
            <div className="text-xs text-[#575A57]">
              Convention tarifaire employés · {data.distributor_name}
              {data.distributor_city ? ` · ${data.distributor_city}` : ""}
            </div>
          </div>
          <Button
            onClick={() => window.print()}
            className="bg-[#8C6B2F] hover:bg-[#735524] text-white no-print"
            data-testid="print-corporate-menu"
          >
            <Printer className="w-4 h-4 mr-2" /> Imprimer
          </Button>
        </div>

        {/* Section 1 : Prix employés */}
        <div className="px-8 py-6">
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
            <h2 className="text-base font-semibold text-[#1A1C1A] uppercase tracking-wide">
              Prix employés — catalogue négocié
            </h2>
            {typeof data.count_total === "number" && (
              <span className="text-xs text-[#575A57]" data-testid="corporate-menu-counter">
                <span className="font-semibold text-[#1F4E3D]">{data.count_installed || 0}</span>
                {" "}actuellement dans la machine · {data.count_total} au catalogue négocié
              </span>
            )}
          </div>
          {data.employee_discount_percent && (
            <p className="text-xs text-[#575A57] mb-2">
              Remise globale employé : <b>{data.employee_discount_percent}%</b> sur les produits sans prix spécifique.
            </p>
          )}
          <p className="text-xs text-[#575A57] mb-4 italic">
            Les articles marqués <span className="inline-block text-[9px] uppercase tracking-wider text-[#1F4E3D] bg-[#E8F2EC] border border-[#A8C9B6] rounded px-1 py-0.5 font-semibold mx-1">Sur machine</span>
            sont actuellement disponibles. Les autres sont au catalogue négocié et peuvent être demandés en réassort.
          </p>

          {Object.entries(grouped).map(([category, items]) => (
            <div key={category} className="mb-5" data-testid={`menu-category-${category}`}>
              {category && category !== "Autres" && (
                <div className="text-xs font-semibold uppercase tracking-wider text-[#8C6B2F] mb-1.5">
                  {category}
                </div>
              )}
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-xs text-[#575A57] border-b border-[#E5E5E0]">
                    <th className="text-left py-1.5 font-medium">Produit</th>
                    <th className="text-right py-1.5 font-medium w-32 text-[#1F4E3D]">Prix employé</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr
                      key={it.product_id}
                      className={`border-b border-[#F0F0EB] ${!it.has_override ? "opacity-70" : ""}`}
                      data-testid={`corporate-item-${it.product_id}`}
                    >
                      <td className="py-1.5">
                        <div className="font-medium text-[#1A1C1A] flex items-center gap-1.5">
                          {it.name}
                          {it.is_current_on_machine && (
                            <span
                              className="inline-block text-[9px] uppercase tracking-wider text-[#1F4E3D] bg-[#E8F2EC] border border-[#A8C9B6] rounded px-1.5 py-0.5 font-semibold"
                              title="Actuellement chargé sur votre machine"
                            >
                              Sur machine
                            </span>
                          )}
                        </div>
                        {it.portion_size && (
                          <div className="text-[10px] text-[#A3A6A3]">{it.portion_size} g</div>
                        )}
                      </td>
                      <td className="py-1.5 text-right font-mono font-semibold text-[#1F4E3D]">
                        {it.has_override ? (
                          <>
                            {fmtCHF(it.price_employee)}
                            <span className="ml-1 text-[9px] text-[#8C6B2F] font-normal">★</span>
                          </>
                        ) : (
                          <span className="italic text-[#A3A6A3] font-normal text-xs">
                            à définir
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          <p className="text-[10px] text-[#A3A6A3] italic mt-3">
            ★ = Prix négocié pour votre entreprise. Les plats marqués « à définir »
            sont au catalogue de Maison Thaï — leur prix employé sera fixé sur la
            base d&apos;une fourchette relative selon la marge, dans le cadre de
            votre contrat.
          </p>
        </div>

        {/* Section 2 : Tranches CA */}
        {(data.corporate_participation_tiers || []).length > 0 && (
          <div className="px-8 py-6 bg-[#FAFAF7] border-t-2 border-[#F0E4C9]">
            <h2 className="text-base font-semibold text-[#1A1C1A] mb-1 uppercase tracking-wide">
              Participation mensuelle
            </h2>
            <p className="text-xs text-[#575A57] mb-3">
              Le montant à verser dépend du chiffre d&apos;affaires réalisé sur le mois.
              {data.corporate_tva_rate > 0 && (
                <> TVA {data.corporate_tva_rate}% en sus.</>
              )}
            </p>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-xs text-[#575A57] border-b border-[#E5E5E0]">
                  <th className="text-left py-1.5 font-medium">CA mensuel réalisé</th>
                  <th className="text-right py-1.5 font-medium w-40">Participation HT</th>
                  {data.corporate_tva_rate > 0 && (
                    <th className="text-right py-1.5 font-medium w-40">Total TTC</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {data.corporate_participation_tiers.map((t, idx) => {
                  const ht = Number(t.participation_chf || 0);
                  const ttc = ht * (1 + Number(data.corporate_tva_rate || 0) / 100);
                  const range = t.ca_max == null
                    ? `Dès ${fmtCHF(t.ca_min)}`
                    : `${fmtCHF(t.ca_min)} — ${fmtCHF(t.ca_max)}`;
                  return (
                    <tr key={idx} className="border-b border-[#F0F0EB]">
                      <td className="py-1.5">{range}</td>
                      <td className="py-1.5 text-right font-mono font-semibold">{fmtCHF(ht)}</td>
                      {data.corporate_tva_rate > 0 && (
                        <td className="py-1.5 text-right font-mono text-[#1F4E3D]">{fmtCHF(ttc)}</td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="px-8 py-4 text-[10px] text-[#A3A6A3] text-center border-t border-[#E5E5E0]">
          Document généré le {new Date(data.generated_at).toLocaleString("fr-CH")}
          {" · "}
          Maison Thaï · Pongsak Phimrat · CH-1964 Conthey · TVA CHE-291.717.594
        </div>
      </div>
    </div>
  );
}
